import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  Button,
  FormField,
  FormSelect,
  FormTextarea,
  Checkbox,
  Label,
  Badge,
  QueryStateBoundary,
  ConfirmDialog,
} from "@/components/ui";
import { ChipToggleGroup } from "./ChipToggleGroup";
import { toApiInput } from "./inquiryForm";
import {
  InquiryFormSchema,
  EVENT_TYPES,
  EVENT_TYPE_LABELS,
  SOURCES,
  SOURCE_LABELS,
  GREEN_FLAGS,
  RED_FLAGS,
  EMPTY_INQUIRY_FORM_VALUES,
  type InquiryFormValues,
  type Inquiry,
} from "@/api/types/inquiry";
import {
  useInquiry,
  useUpdateInquiry,
  useLogFirstReply,
  useDeleteInquiry,
} from "@/api/hooks/useInquiries";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { formatDateTime } from "./format";

interface InquiryDetailDialogProps {
  inquiryId: string | null;
  onClose: () => void;
}

function toFormValues(inquiry: Inquiry): InquiryFormValues {
  return {
    contact_name: inquiry.contact_name,
    event_date: inquiry.event_date ?? "",
    event_type: inquiry.event_type ?? "",
    guest_count: inquiry.guest_count != null ? String(inquiry.guest_count) : "",
    venue_name: inquiry.venue_name ?? "",
    source: inquiry.source ?? "",
    na_guests_expected: inquiry.na_guests_expected ?? false,
    budget_band: inquiry.budget_band ?? "",
    message: inquiry.message ?? "",
  };
}

export function InquiryDetailDialog({ inquiryId, onClose }: InquiryDetailDialogProps) {
  const open = !!inquiryId;
  const navigate = useNavigate();
  const { data: inquiry, isLoading, error, refetch } = useInquiry(inquiryId ?? undefined);
  const updateInquiry = useUpdateInquiry();
  const logFirstReply = useLogFirstReply();
  const deleteInquiry = useDeleteInquiry();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Quotes live in their own feature (Phase 2) — hand off via the URL rather
  // than importing the quote builder dialog here, so the two features stay
  // decoupled. QuotesPage reads ?inquiry= and auto-opens the builder.
  function handleCreateQuote() {
    if (!inquiry) return;
    onClose();
    navigate(`/quotes?inquiry=${inquiry.id}`);
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<InquiryFormValues>({
    resolver: zodResolver(InquiryFormSchema),
    defaultValues: EMPTY_INQUIRY_FORM_VALUES,
  });

  useEffect(() => {
    if (inquiry) reset(toFormValues(inquiry));
  }, [inquiry, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!inquiry) return;
    try {
      await updateInquiry.mutateAsync({ id: inquiry.id, ...toApiInput(values) });
      toastSuccess("Inquiry updated");
    } catch (err) {
      toastError("Couldn't update inquiry", getErrorMessage(err));
    }
  });

  function toggleFlag(group: "green_flags" | "red_flags", id: string) {
    if (!inquiry) return;
    const current = inquiry.qualification[group];
    const next = current.includes(id)
      ? current.filter((flag) => flag !== id)
      : [...current, id];

    updateInquiry.mutate(
      {
        id: inquiry.id,
        qualification: {
          green_flags: group === "green_flags" ? next : inquiry.qualification.green_flags,
          red_flags: group === "red_flags" ? next : inquiry.qualification.red_flags,
        },
      },
      {
        onError: (err) =>
          toastError("Couldn't update qualification", getErrorMessage(err)),
      },
    );
  }

  async function handleLogReply() {
    if (!inquiry) return;
    try {
      await logFirstReply.mutateAsync(inquiry.id);
      toastSuccess("First reply logged");
    } catch (err) {
      toastError("Couldn't log reply", getErrorMessage(err));
    }
  }

  async function handleDelete() {
    if (!inquiry) return;
    try {
      await deleteInquiry.mutateAsync(inquiry.id);
      toastSuccess("Inquiry deleted");
      setConfirmingDelete(false);
      onClose();
    } catch (err) {
      // getErrorMessage already prefers the API's real `detail` string (e.g.
      // the 409 cascade guard's "booked event with invoices" message) over a
      // generic fallback — see api/client.ts.
      toastError("Couldn't delete inquiry", getErrorMessage(err));
    }
  }

  const slaInfo = useMemo(() => {
    if (!inquiry) return null;
    if (inquiry.first_reply_at) {
      return {
        text: `First replied ${formatDateTime(inquiry.first_reply_at)}`,
        breached: false,
      };
    }
    if (!inquiry.sla_due_at) return null;
    const breached = new Date(inquiry.sla_due_at).getTime() < Date.now();
    return {
      text: breached
        ? `SLA breached — reply was due ${formatDateTime(inquiry.sla_due_at)}`
        : `Reply due ${formatDateTime(inquiry.sla_due_at)}`,
      breached,
    };
  }, [inquiry]);

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Inquiry detail">
      <DialogContent size="xl">
        <DialogHeader onClose={onClose}>{inquiry?.contact_name ?? "Inquiry"}</DialogHeader>
        <DialogBody className="space-y-6">
          <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
            {inquiry && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {slaInfo && (
                    <p
                      className={
                        slaInfo.breached
                          ? "text-sm font-medium text-danger"
                          : "text-sm text-text-secondary"
                      }
                    >
                      {slaInfo.text}
                    </p>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    {!inquiry.first_reply_at && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => void handleLogReply()}
                        disabled={logFirstReply.isPending}
                      >
                        {logFirstReply.isPending ? "Logging..." : "Log first reply"}
                      </Button>
                    )}
                    <Button size="sm" onClick={handleCreateQuote}>
                      Create quote
                    </Button>
                    <Button
                      variant="ghost-danger"
                      size="sm"
                      onClick={() => setConfirmingDelete(true)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                {inquiry.notify_status === "failed" && (
                  <div className="min-w-0 rounded-md border border-danger/30 bg-danger/5 px-3 py-2">
                    <p className="min-w-0 break-words text-sm text-danger">
                      <span className="font-medium">Lead alert email failed to send. </span>
                      {inquiry.notify_error}
                    </p>
                  </div>
                )}

                {inquiry.stage === "closed_lost" && inquiry.lost_reason && (
                  <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2">
                    <p className="text-sm text-danger">
                      <span className="font-medium">Lost reason: </span>
                      {inquiry.lost_reason}
                    </p>
                  </div>
                )}

                <form onSubmit={onSubmit} noValidate className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      label="Contact name"
                      required
                      error={errors.contact_name?.message}
                      {...register("contact_name")}
                    />
                    <FormField
                      label="Event date"
                      type="date"
                      error={errors.event_date?.message}
                      {...register("event_date")}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormSelect
                      label="Event type"
                      error={errors.event_type?.message}
                      {...register("event_type")}
                    >
                      <option value="">Not sure yet</option>
                      {EVENT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {EVENT_TYPE_LABELS[t]}
                        </option>
                      ))}
                    </FormSelect>
                    <FormField
                      label="Guest count"
                      type="number"
                      min={0}
                      error={errors.guest_count?.message}
                      {...register("guest_count")}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      label="Venue name"
                      error={errors.venue_name?.message}
                      {...register("venue_name")}
                    />
                    <FormSelect
                      label="Source"
                      error={errors.source?.message}
                      {...register("source")}
                    >
                      <option value="">Unknown</option>
                      {SOURCES.map((s) => (
                        <option key={s} value={s}>
                          {SOURCE_LABELS[s]}
                        </option>
                      ))}
                    </FormSelect>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end">
                    <FormField
                      label="Budget band"
                      error={errors.budget_band?.message}
                      {...register("budget_band")}
                    />
                    <div className="pb-2.5">
                      <Checkbox
                        label="NA guests expected"
                        {...register("na_guests_expected")}
                      />
                    </div>
                  </div>

                  <FormTextarea
                    label="Message"
                    error={errors.message?.message}
                    {...register("message")}
                  />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={updateInquiry.isPending || !isDirty}>
                      {updateInquiry.isPending ? "Saving..." : "Save changes"}
                    </Button>
                  </div>
                </form>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label>Green flags</Label>
                    <Badge variant="success" size="sm">
                      {inquiry.qualification.green_flags.length}
                    </Badge>
                  </div>
                  <ChipToggleGroup
                    options={GREEN_FLAGS}
                    selected={inquiry.qualification.green_flags}
                    onToggle={(id) => toggleFlag("green_flags", id)}
                    variant="green"
                    disabled={updateInquiry.isPending}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label>Red flags</Label>
                    <Badge variant="danger" size="sm">
                      {inquiry.qualification.red_flags.length}
                    </Badge>
                  </div>
                  <ChipToggleGroup
                    options={RED_FLAGS}
                    selected={inquiry.qualification.red_flags}
                    onToggle={(id) => toggleFlag("red_flags", id)}
                    variant="red"
                    disabled={updateInquiry.isPending}
                  />
                </div>
              </>
            )}
          </QueryStateBoundary>
        </DialogBody>
      </DialogContent>

      {inquiry && (
        <ConfirmDialog
          open={confirmingDelete}
          onClose={() => setConfirmingDelete(false)}
          onConfirm={() => void handleDelete()}
          title="Delete inquiry"
          message={`Delete the inquiry from ${inquiry.contact_name}? This can't be undone.`}
          confirmLabel="Delete"
          variant="danger"
          isLoading={deleteInquiry.isPending}
        />
      )}
    </Dialog>
  );
}
